import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getAuthHeader } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { 
  TrendingUp, TrendingDown, DollarSign, Truck, Users, Briefcase,
  BarChart3, PieChartIcon, Activity, Target
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const COLORS = ['#3b82f6', '#22c55e', '#f97316', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f59e0b'];

const STATUS_COLORS = {
  delivered: '#22c55e',
  new: '#3b82f6',
  assigned: '#8b5cf6',
  on_the_way: '#f97316',
  arrived: '#14b8a6',
  cancelled: '#ef4444',
  failed_attempt: '#f59e0b',
  rescheduled: '#6366f1'
};

const formatCurrency = (value) => {
  return new Intl.NumberFormat('ar-IQ', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

const KPICard = ({ title, value, change, icon: Icon, color, prefix = '', suffix = '' }) => {
  const isPositive = change >= 0;
  
  return (
    <Card className="hover:shadow-lg transition-shadow">
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500 mb-1">{title}</p>
            <p className="text-2xl font-bold">
              {prefix}{typeof value === 'number' ? formatCurrency(value) : value}{suffix}
            </p>
            {change !== undefined && (
              <div className={`flex items-center gap-1 mt-2 text-sm ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
                {isPositive ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
                <span>{Math.abs(change)}% عن الشهر السابق</span>
              </div>
            )}
          </div>
          <div className={`p-4 rounded-xl ${color}`}>
            <Icon size={28} className="text-white" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const AnalyticsDashboard = () => {
  const [period, setPeriod] = useState('month');
  const [loading, setLoading] = useState(true);
  const [kpis, setKpis] = useState(null);
  const [salesData, setSalesData] = useState([]);
  const [revenueComparison, setRevenueComparison] = useState([]);
  const [deliveryStats, setDeliveryStats] = useState(null);
  const [clientsStats, setClientsStats] = useState(null);
  const [financialSummary, setFinancialSummary] = useState(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchAllData();
  }, [period]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [kpisRes, salesRes, revenueRes, deliveryRes, clientsRes, financialRes] = await Promise.all([
        axios.get(`${API}/api/analytics/kpis`, { headers: getAuthHeader() }),
        axios.get(`${API}/api/analytics/sales-chart?period=${period}`, { headers: getAuthHeader() }),
        axios.get(`${API}/api/analytics/revenue-comparison`, { headers: getAuthHeader() }),
        axios.get(`${API}/api/analytics/delivery-stats?period=${period}`, { headers: getAuthHeader() }),
        axios.get(`${API}/api/analytics/clients-stats?period=${period}`, { headers: getAuthHeader() }),
        axios.get(`${API}/api/analytics/financial-summary?period=${period}`, { headers: getAuthHeader() })
      ]);

      setKpis(kpisRes.data);
      setSalesData(salesRes.data);
      setRevenueComparison(revenueRes.data);
      setDeliveryStats(deliveryRes.data);
      setClientsStats(clientsRes.data);
      setFinancialSummary(financialRes.data);
    } catch (error) {
      toast.error('فشل في تحميل البيانات');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Activity className="animate-spin h-12 w-12 text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" dir="rtl">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl">
            <BarChart3 className="text-white" size={28} />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-800">لوحة التحكم الإحصائية</h2>
            <p className="text-gray-500 text-sm">تحليلات شاملة للأعمال</p>
          </div>
        </div>
        
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="week">آخر أسبوع</SelectItem>
            <SelectItem value="month">آخر شهر</SelectItem>
            <SelectItem value="quarter">آخر 3 أشهر</SelectItem>
            <SelectItem value="year">آخر سنة</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* KPIs */}
      {kpis && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <KPICard
            title="الإيرادات"
            value={kpis.revenue?.current || 0}
            change={kpis.revenue?.change}
            icon={DollarSign}
            color="bg-gradient-to-br from-green-500 to-emerald-600"
            suffix=" د.ع"
          />
          <KPICard
            title="التوصيلات"
            value={kpis.deliveries?.current || 0}
            change={kpis.deliveries?.change}
            icon={Truck}
            color="bg-gradient-to-br from-blue-500 to-cyan-600"
          />
          <KPICard
            title="نسبة نجاح التوصيل"
            value={kpis.delivery_success_rate?.current || 0}
            icon={Target}
            color="bg-gradient-to-br from-purple-500 to-pink-600"
            suffix="%"
          />
          <KPICard
            title="عملاء جدد"
            value={kpis.new_clients?.current || 0}
            change={kpis.new_clients?.change}
            icon={Users}
            color="bg-gradient-to-br from-orange-500 to-red-600"
          />
        </div>
      )}

      {/* Tabs */}
      <Tabs defaultValue="sales" className="space-y-4">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="sales">المبيعات</TabsTrigger>
          <TabsTrigger value="delivery">التوصيل</TabsTrigger>
          <TabsTrigger value="clients">العملاء</TabsTrigger>
          <TabsTrigger value="financial">المالية</TabsTrigger>
        </TabsList>

        {/* Sales Tab */}
        <TabsContent value="sales" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Sales Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity size={20} className="text-blue-600" />
                  المبيعات اليومية
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={salesData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} tickFormatter={formatCurrency} />
                    <Tooltip 
                      formatter={(value) => [formatCurrency(value) + ' د.ع', '']}
                      labelFormatter={(label) => `التاريخ: ${label}`}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="sales" 
                      stroke="#3b82f6" 
                      fill="url(#colorSales)" 
                      name="المبيعات"
                    />
                    <Area 
                      type="monotone" 
                      dataKey="paid" 
                      stroke="#22c55e" 
                      fill="url(#colorPaid)" 
                      name="المدفوع"
                    />
                    <defs>
                      <linearGradient id="colorSales" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                      </linearGradient>
                      <linearGradient id="colorPaid" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#22c55e" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <Legend />
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Monthly Revenue Comparison */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 size={20} className="text-purple-600" />
                  مقارنة الإيرادات الشهرية
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={revenueComparison}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month_name" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} tickFormatter={formatCurrency} />
                    <Tooltip formatter={(value) => [formatCurrency(value) + ' د.ع', 'الإيرادات']} />
                    <Bar dataKey="revenue" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Delivery Tab */}
        <TabsContent value="delivery" className="space-y-4">
          {deliveryStats && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Status Distribution */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <PieChartIcon size={20} className="text-orange-600" />
                    توزيع حالات التوصيل
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={deliveryStats.status_distribution}
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        innerRadius={60}
                        fill="#8884d8"
                        dataKey="count"
                        nameKey="label"
                        label={({ label, percent }) => `${label} (${(percent * 100).toFixed(0)}%)`}
                      >
                        {deliveryStats.status_distribution.map((entry, index) => (
                          <Cell 
                            key={`cell-${index}`} 
                            fill={STATUS_COLORS[entry.status] || COLORS[index % COLORS.length]} 
                          />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => [value, 'العدد']} />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Daily Deliveries */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Truck size={20} className="text-blue-600" />
                    التوصيلات اليومية
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={deliveryStats.daily_deliveries}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip />
                      <Line type="monotone" dataKey="total" stroke="#3b82f6" name="الإجمالي" strokeWidth={2} />
                      <Line type="monotone" dataKey="delivered" stroke="#22c55e" name="تم التسليم" strokeWidth={2} />
                      <Legend />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Driver Performance */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users size={20} className="text-green-600" />
                    أداء السائقين
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={deliveryStats.driver_performance} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" tick={{ fontSize: 12 }} />
                      <YAxis dataKey="name" type="category" tick={{ fontSize: 12 }} width={80} />
                      <Tooltip />
                      <Bar dataKey="delivered" fill="#22c55e" name="تم التسليم" radius={[0, 4, 4, 0]} />
                      <Bar dataKey="total" fill="#3b82f6" name="الإجمالي" radius={[0, 4, 4, 0]} />
                      <Legend />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Area Distribution */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Target size={20} className="text-purple-600" />
                    التوزيع حسب المنطقة
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={deliveryStats.area_distribution}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" tick={{ fontSize: 11 }} angle={-45} textAnchor="end" height={60} />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip formatter={(value) => [value, 'عدد التوصيلات']} />
                      <Bar dataKey="count" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        {/* Clients Tab */}
        <TabsContent value="clients" className="space-y-4">
          {clientsStats && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Top Clients */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users size={20} className="text-blue-600" />
                    أفضل العملاء (حسب الإيرادات)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={clientsStats.top_clients} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" tick={{ fontSize: 12 }} tickFormatter={formatCurrency} />
                      <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={100} />
                      <Tooltip formatter={(value) => [formatCurrency(value) + ' د.ع', 'الإيرادات']} />
                      <Bar dataKey="revenue" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Monthly New Clients */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp size={20} className="text-green-600" />
                    العملاء الجدد شهرياً
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <AreaChart data={clientsStats.monthly_new_clients}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip formatter={(value) => [value, 'عملاء جدد']} />
                      <Area 
                        type="monotone" 
                        dataKey="count" 
                        stroke="#22c55e" 
                        fill="url(#colorClients)" 
                      />
                      <defs>
                        <linearGradient id="colorClients" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#22c55e" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                    </AreaChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        {/* Financial Tab */}
        <TabsContent value="financial" className="space-y-4">
          {financialSummary && (
            <>
              {/* Financial KPIs */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="bg-gradient-to-br from-green-50 to-emerald-100 border-green-200">
                  <CardContent className="p-4 text-center">
                    <p className="text-sm text-green-600 mb-1">إجمالي المبيعات</p>
                    <p className="text-xl font-bold text-green-700">{formatCurrency(financialSummary.total_sales)} د.ع</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-blue-50 to-cyan-100 border-blue-200">
                  <CardContent className="p-4 text-center">
                    <p className="text-sm text-blue-600 mb-1">المدفوع</p>
                    <p className="text-xl font-bold text-blue-700">{formatCurrency(financialSummary.paid_sales)} د.ع</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-red-50 to-orange-100 border-red-200">
                  <CardContent className="p-4 text-center">
                    <p className="text-sm text-red-600 mb-1">المصروفات</p>
                    <p className="text-xl font-bold text-red-700">{formatCurrency(financialSummary.total_expenses)} د.ع</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-purple-50 to-pink-100 border-purple-200">
                  <CardContent className="p-4 text-center">
                    <p className="text-sm text-purple-600 mb-1">صافي الربح</p>
                    <p className="text-xl font-bold text-purple-700">{formatCurrency(financialSummary.net_profit)} د.ع</p>
                    <p className="text-xs text-purple-500">({financialSummary.profit_margin}%)</p>
                  </CardContent>
                </Card>
              </div>

              {/* Expense Breakdown */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <PieChartIcon size={20} className="text-red-600" />
                    تفصيل المصروفات
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={financialSummary.expense_breakdown}
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="amount"
                        nameKey="category"
                        label={({ category, percent }) => `${category} (${(percent * 100).toFixed(0)}%)`}
                      >
                        {financialSummary.expense_breakdown.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => [formatCurrency(value) + ' د.ع', '']} />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AnalyticsDashboard;
